import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { bookingsApi } from '../../../api/adminApi';
import './BookingView.css';

function BookingView() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError('');
    bookingsApi
      .getOne(id)
      .then((data) => setBooking(data))
      .catch((err) => setError(err.userMessage || 'Failed to load booking'))
      .finally(() => setLoading(false));
  }, [id]);

  const formatDate = (str) => {
    if (!str) return '—';
    try {
      const d = new Date(str);
      return isNaN(d.getTime()) ? str : d.toLocaleDateString(undefined, { dateStyle: 'long', timeStyle: 'short' });
    } catch {
      return str;
    }
  };

  const formatDateTime = (str) => {
    if (!str) return '—';
    try {
      const d = new Date(str);
      if (isNaN(d.getTime())) return str;
      return d.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch {
      return str;
    }
  };

  const formatTimeRange = (start, end) => {
    if (!start) return '—';
    try {
      const dStart = new Date(start);
      if (isNaN(dStart.getTime())) return start;
      const timeStart = dStart.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
      if (!end) return timeStart;
      const dEnd = new Date(end);
      if (isNaN(dEnd.getTime())) return timeStart;
      const timeEnd = dEnd.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
      return `${timeStart} – ${timeEnd}`;
    } catch {
      return start;
    }
  };

  const field = (label, value) => (
    <div key={label} className="admin-booking-view-row">
      <dt>{label}</dt>
      <dd>{value ?? '—'}</dd>
    </div>
  );

  if (loading) {
    return <div className="admin-booking-view-loading">Loading booking…</div>;
  }

  if (error || !booking) {
    return (
      <div className="admin-booking-view">
        <div className="admin-booking-view-header">
          <Link to="/admin/bookings" className="admin-booking-view-back">← Bookings</Link>
        </div>
        <div className="admin-booking-view-error" role="alert">
          {error || 'Booking not found.'}
        </div>
      </div>
    );
  }

  const slotDate = booking.slot_start ?? booking.appointment_date ?? booking.date ?? booking.scheduled_at;
  const title = slotDate ? formatDate(slotDate) : (booking.parent_name ? `${booking.parent_name} – ${booking.child_name ?? 'Booking'}` : 'Booking');

  return (
    <div className="admin-booking-view">
      <div className="admin-booking-view-header">
        <Link to="/admin/bookings" className="admin-booking-view-back">← Bookings</Link>
        <h2>{title}</h2>
      </div>

      <div className="admin-booking-view-card">
        <dl className="admin-booking-view-details">
          {field('Parent Name', booking.parent_name ?? booking.name ?? booking.full_name)}
          {field('Child Name', booking.child_name ?? booking.childName)}
          {field('Child Age', booking.child_age ?? booking.childAge)}
          {field('Email', booking.email ?? booking.parent_email)}
          {field('Phone', booking.phone_number ?? booking.phone)}
          {field('Slot Date', formatDateTime(booking.slot_start ?? booking.appointment_date ?? booking.date ?? booking.scheduled_at))}
          {field('Slot Time', booking.slot_start ? formatTimeRange(booking.slot_start, booking.slot_end) : (booking.appointment_time ?? booking.time ?? booking.slot ?? '—'))}
          <div className="admin-booking-view-row admin-booking-view-row--message">
            <dt>Notes</dt>
            <dd className="admin-booking-view-notes">
              {booking.message ?? booking.notes ?? booking.additional_info ?? '—'}
            </dd>
          </div>
          {field('Status', booking.status)}
          {field('Created', formatDateTime(booking.created_at ?? booking.createdAt))}
          {field('Updated', formatDateTime(booking.updated_at ?? booking.updatedAt))}
        </dl>
      </div>
    </div>
  );
}

export default BookingView;
